import { Fragment, useEffect, useMemo, useState } from "react";

import { PalIconButton, PalTypography } from "@palamar/fe-library";
import { useFormik } from "formik";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import * as Yup from "yup";

import { Cancel, Menu, NavigateBefore, NavigateNext } from "@mui/icons-material";
import { Box, CircularProgress, Drawer, Grid, LinearProgress, useMediaQuery } from "@mui/material";

import { getBotSettings, updateBotSettings } from "~apiRequests/bot";
import { getIntentDetails } from "~apiRequests/intent";
import { getInvitedUsers, postInviteUser } from "~apiRequests/permissions";
import { ReactComponent as Img1 } from "~assets/images/onboarding/1.svg";
import { ReactComponent as Logo } from "~assets/images/palmate-logo.svg";
import { useDebouncedEffect } from "~common/hooks/useDebounceEffect";
import useLoading from "~common/hooks/useLoading";
import useQueryParams from "~common/hooks/useQueryParams";
import PalLink from "~components/mui/PalLink";
import {
  eCommerceProject,
  OnboardingContentSeparator,
  onboardingProjectStatus,
  onboardingRagType,
  onboardingStatus,
  popupChatWidthDefault,
  userRole,
} from "~constants";
import AlertHelper from "~helpers/AlertHelper";
import DateHelper from "~helpers/DateHelper";
import DialogHelper from "~helpers/DialogHelper";
import FileHelper from "~helpers/FileHelper";
import HistoryHelper from "~helpers/HistoryHelper";
import LoadingHelper from "~helpers/LoadingHelper";
import Utils from "~helpers/Utils";
import { getRagState, updateOnboardingStatus, updateRagState } from "~store/onboarding/actions";
import { getChannelList } from "~store/settings/channels/actions";
import { selectChannelList } from "~store/settings/channels/selectors";
import { createOrUpdateCompany, getCompany } from "~store/settings/company/actions";
import { selectCompany } from "~store/settings/company/selectors";
import { getShopifyIntegrations } from "~store/settings/eCommerce/actions";
import { selectECommerceList } from "~store/settings/eCommerce/selectors";
import { getPopupSettings, initPopupSettingsState, patchPopupSettings } from "~store/settings/popupSettings/actions";
import { selectPopupSettingsState } from "~store/settings/popupSettings/selectors";
import {
  createOrUpdateProject,
  getProject,
  setProject,
  updateProjectSettings,
} from "~store/settings/projectSettings/actions";
import { selectProject } from "~store/settings/projectSettings/selectors";
import { updateCallcenterSettings } from "~store/socket/livechat/actions";
import { selectCallcenterSettings } from "~store/socket/livechat/selectors";
import { selectUserInfo } from "~store/user/selectors";

import {
  HeaderBarGrid,
  headerHeight,
  LogoWrapper,
  NavigateButton,
  PanelLeft,
  PanelRight,
  panelRightWidthLarge,
  panelRightWidthSmall,
  RightImgWrapper,
  RightStepInfo,
  setPreviewSettings,
  stepbarHeight,
  StepFooterContainerGrid,
  StyledDiv,
} from "./CommonComponents";
import StepBasic from "./steps/StepBasic";
import StepBotSettings from "./steps/StepBotSettings";
import StepChannels from "./steps/StepChannels";
import StepChatbot from "./steps/StepChatbot";
import StepECommerce from "./steps/StepECommerce";
import StepGeneralInformation from "./steps/StepGeneralInformation";
import StepLivechat from "./steps/StepLivechat";
import StepProjectSelect from "./steps/StepProjectSelect";
import StepRag from "./steps/StepRag";
import StepShopifyCompleted from "./steps/StepShopifyCompleted";
import StepShopifyIntegration from "./steps/StepShopifyIntegration";

const stepsDefinition = {
  stepProjectSelect: {
    key: "stepProjectSelect",
    order: 0,
    component: StepProjectSelect,
    isOptional: false,
  },
  stepShopifyIntegration: {
    key: "stepShopifyIntegration",
    order: 1,
    component: StepShopifyIntegration,
    isOptional: false,
  },
  stepBasic: {
    key: "stepBasic",
    order: 2,
    component: StepBasic,
    isOptional: false,
  },
  stepGeneralInformation: {
    key: "stepGeneralInformation",
    order: 3,
    component: StepGeneralInformation,
    isOptional: false,
  },
  stepBotSettings: {
    key: "stepBotSettings",
    order: 4,
    component: StepBotSettings,
    isOptional: false,
  },
  stepLivechat: {
    key: "stepLivechat",
    order: 5,
    component: StepLivechat,
    isOptional: true,
  },
  stepChatbot: {
    key: "stepChatbot",
    order: 6,
    component: StepChatbot,
    isOptional: false,
  },
  stepRag: {
    key: "stepRag",
    order: 7,
    component: StepRag,
    isOptional: false,
  },
  stepChannels: {
    key: "stepChannels",
    order: 8,
    component: StepChannels,
    isOptional: true,
  },
  stepECommerce: {
    key: "stepECommerce",
    order: 9,
    component: StepECommerce,
    isOptional: true,
  },
  stepShopifyCompleted: {
    key: "stepShopifyCompleted",
    order: 10,
    component: StepShopifyCompleted,
    isOptional: false,
  },
};

const IFRAME_PREVIEW_ID = "popup-preview";
function Onboarding() {
  const { id: activeProjectId } = useParams();
  const { company: companyId } = useQueryParams();

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const location = useLocation();

  const searchParams = useQueryParams();
  const { shopifyCode, shopifyInt, shopifyConnDone, shopifyConnId } = searchParams;
  const isShopifyOnboarding = shopifyInt === "true";
  const isShopifyConnectedDone = shopifyConnDone === "true";

  const userInfo = useSelector(selectUserInfo);
  const callcenterSettings = useSelector(selectCallcenterSettings);
  const popupSettingsState = useSelector(selectPopupSettingsState);
  const project = useSelector(selectProject);
  const company = useSelector(selectCompany);
  const channelList = useSelector(selectChannelList);
  const eCommerceList = useSelector(selectECommerceList);
  // const shopifyIntegrations = useSelector(selectShopifyIntegrations);

  const [debouncedLoadingDone, setDebouncedLoadingDone] = useState(false);
  const [loading, q] = useLoading(true, [activeProjectId ? project?.id : true]);

  const stepsList = useMemo(() => {
    const allSteps = Object.values(stepsDefinition).sort((a, b) => a.order - b.order);
    const filteredSteps = allSteps.filter((step) => {
      if (step.key === stepsDefinition.stepProjectSelect.key) {
        // return userInfo.project_roles?.length > 1 && isShopifyOnboarding;
        return false;
      }
      if (isShopifyConnectedDone) {
        return step.key === stepsDefinition.stepShopifyCompleted.key;
      }

      if (step.key === stepsDefinition.stepShopifyIntegration.key) {
        return isShopifyOnboarding;
      }
      if (step.key === stepsDefinition.stepECommerce.key) {
        return !isShopifyOnboarding;
      }
      if (step.key === stepsDefinition.stepShopifyCompleted.key) {
        return isShopifyOnboarding;
      }
      return true;
    });
    return filteredSteps;
  }, [isShopifyOnboarding, isShopifyConnectedDone]);

  const defaultStep = useMemo(() => {
    return stepsList[0].key;
  }, [stepsList]);

  const [currentStepKey, setCurrentStepKey] = useState(defaultStep);
  const [invitedUserList, setInvitedUserList] = useState([]);
  const [hasIframeError, setHasIframeError] = useState(false);
  const [drawerVisibility, setDrawerVisibility] = useState(false);
  const [direction, setDirection] = useState(1);
  const [isPreviewBotReady, setIsPreviewBotReady] = useState(false);
  const [botSettings, setBotSettings] = useState(null);
  const [isBottomActionsDisabled, setIsBottomActionsDisabled] = useState(false);

  const { currentStepIndex, currentStepItem, nextStepItem, previousStepItem } = useMemo(() => {
    const currentStepIndex = stepsList.findIndex((item) => item.key === currentStepKey);
    const currentStepItem = stepsList[currentStepIndex];
    const nextStepItem = stepsList[currentStepIndex + 1];
    const previousStepItem = stepsList[currentStepIndex - 1];

    return { currentStepIndex, currentStepItem, nextStepItem, previousStepItem };
  }, [currentStepKey, stepsList]);

  useDebouncedEffect(
    () => {
      if (loading) return;
      setDebouncedLoadingDone(true);
    },
    [loading],
    500
  );

  useEffect(() => {
    dispatch(getShopifyIntegrations());
  }, [dispatch]);

  useEffect(() => {
    const mainBot = project?.settings?.main_bot;
    if (!mainBot || !project?.id) return;

    q(getBotSettings(project.id, mainBot)).then((response) => {
      setBotSettings(response);
    });
  }, [project?.settings?.main_bot, q, project?.id]);

  useEffect(() => {
    if (!project?.id) return;
    q(dispatch(getCompany(project?.company)));
  }, [q, dispatch, project?.id, project?.company]);

  useEffect(() => {
    if (!userInfo.is_superuser && !companyId) {
      const firstProject = userInfo.project_roles?.[0]?.project;
      if (firstProject && !isShopifyOnboarding) {
        HistoryHelper.replace(`/dashboard/setup/${firstProject}`, {
          state: location.state,
          search: Utils.qs({
            ...searchParams,
          }),
        });
      }
    }
  }, [t, activeProjectId, userInfo, companyId, location.state, searchParams, dispatch, q, isShopifyOnboarding]);

  useEffect(() => {
    if (activeProjectId) {
      q(dispatch(getProject(activeProjectId)));
      return () => {
        // Reset the project state when leaving the onboarding page
        dispatch(setProject({}));
      };
    }
  }, [t, q, dispatch, activeProjectId]);

  useEffect(() => {
    if (project?.id) {
      q(dispatch(getProject(project?.id)));
      q(dispatch(getChannelList(project?.id)));

      q(dispatch(getPopupSettings(project?.id, { skipUpdate: true }))).then(() => {
        if (project.status === onboardingProjectStatus.ONB) {
          dispatch(initPopupSettingsState());
        }
      });
    }
  }, [t, q, dispatch, project?.id, project?.status]);

  const { whatsappData, messengerData, telegramData, instagramData } = useMemo(() => {
    const whatsappData = channelList.find((item) => item.integration_type === "whatsapp");
    const messengerData = channelList.find((item) => item.integration_type === "messenger");
    const telegramData = channelList.find((item) => item.integration_type === "telegram");
    const instagramData = channelList.find((item) => item.integration_type === "instagram");
    return { whatsappData, messengerData, telegramData, instagramData };
  }, [channelList]);

  // useEffect(() => {
  //   if (userInfo.project_roles?.length > 0) {
  //     dispatch(getProject(userInfo.project_roles[0].project));
  //   }
  // }, [t, dispatch, userInfo]);

  const isMdDown = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      //regular settings;
      welcomeForm: {
        ...popupSettingsState,
        welcome_msg: popupSettingsState.welcome_msg || t("dashboard.onboarding.welcomeMsg"),
      },

      files: {},

      [stepsDefinition.stepProjectSelect.key]: {
        selected_project: project?.id || null,
      },
      [stepsDefinition.stepShopifyIntegration.key]: {
        integrated: project?.shopify_integration ? true : false,
        shopify_integration: project?.shopify_integration || null,
      },
      [stepsDefinition.stepBasic.key]: {
        assistant_name: project?.name,
        assistant_languages: project?.main_language || project?.settings?.chatbot_languages?.[0] || null,
        assistant_template: null,
        assistant_template_type: null,
        ...(isShopifyOnboarding && {}),
      },
      [stepsDefinition.stepGeneralInformation.key]: {
        assistant_supported_languages: project?.settings?.supported_languages?.map((a) => a.language) || [],
        allowed_mail_domains: company?.allowed_mail_domains?.join("\n") || userInfo.email.split("@")?.[1] || "",
        address: company?.address || "",
        company_logo: company?.company_logo || "",
        company_logo_file: null,

        company_details: {
          // industry: "",
          // company_size: "",
          // usage_count: "",
          // department: "",
          // role: "",
          ...(company?.company_details || {}),
        },
      },
      [stepsDefinition.stepLivechat.key]: {
        agent_can_leave_session: callcenterSettings?.agent_can_leave_session,
        work_start_time: callcenterSettings?.working_hours?.start
          ? DateHelper.getDateTime(callcenterSettings?.working_hours?.start, "HH:mm")
          : DateHelper.getDateTime().startOf("day").add(8, "hours"),
        work_end_time: callcenterSettings?.working_hours?.end
          ? DateHelper.getDateTime(callcenterSettings?.working_hours?.end, "HH:mm")
          : DateHelper.getDateTime().startOf("day").add(18, "hours"),
        agentList: [],
      },
      [stepsDefinition.stepRag.key]: {
        sourceList: [],
        ragIntentId: null,
        ragFactGroup: {},
        ragFactGroupId: null,
      },
      [stepsDefinition.stepBotSettings.key]: {
        bot_name: botSettings?.name || "",
        awareness_prompt: botSettings?.awareness_prompt || "",
      },
      [stepsDefinition.stepChannels.key]: {
        whatsapp: {
          ...whatsappData,
          access_token: whatsappData?.access_token || "",
        },
        messenger: {
          ...messengerData,
          access_token: messengerData?.access_token || "",
        },
        telegram: {
          ...telegramData,
          access_token: telegramData?.access_token || "",
        },
        instagram: {
          ...instagramData,
          access_token: instagramData?.access_token || "",
        },
      },
      [stepsDefinition.stepECommerce.key]: {
        [eCommerceProject.YT]: eCommerceList.find((item) => item.platform_type === eCommerceProject.YT),
        [eCommerceProject.PD]: eCommerceList.find((item) => item.platform_type === eCommerceProject.PD),
        [eCommerceProject.PP]: eCommerceList.find((item) => item.platform_type === eCommerceProject.PP),
        [eCommerceProject.PT]: eCommerceList.find((item) => item.platform_type === eCommerceProject.PT),
        [eCommerceProject.PS]: eCommerceList.find((item) => item.platform_type === eCommerceProject.PS),
        [eCommerceProject.PI]: eCommerceList.find((item) => item.platform_type === eCommerceProject.PI),
        [eCommerceProject.PTS]: eCommerceList.find((item) => item.platform_type === eCommerceProject.PTS),
        [eCommerceProject.YH]: eCommerceList.find((item) => item.platform_type === eCommerceProject.YH),
        [eCommerceProject.NA]: eCommerceList.find((item) => item.platform_type === eCommerceProject.NA),
      },
    },
    validationSchema: Yup.object({
      [stepsDefinition.stepShopifyIntegration.key]: Yup.mixed().when({
        is: () => currentStepKey === stepsDefinition.stepShopifyIntegration.key,
        then: Yup.object({
          shopify_integration: Yup.number().nullable().required(t("common.required")),
        }),
      }),
      [stepsDefinition.stepProjectSelect.key]: Yup.mixed().when({
        is: () => currentStepKey === stepsDefinition.stepProjectSelect.key && isShopifyOnboarding,
        then: Yup.object({
          selected_project: Yup.number().nullable().required(t("common.required")),
        }),
      }),
      [stepsDefinition.stepBasic.key]: Yup.mixed().when({
        is: () => currentStepKey === stepsDefinition.stepBasic.key,
        then: Yup.object({
          assistant_name: Yup.string().required(t("common.required")).min(3, "Must be at least 3 characters"),
          assistant_languages: Yup.string().nullable().required(t("common.required")),
          assistant_template: Yup.number()
            .when({
              is: () => !project?.id && !isShopifyOnboarding,
              then: Yup.number().nullable().required(t("common.required")),
            })
            .nullable(),
        }),
      }),
      [stepsDefinition.stepGeneralInformation.key]: Yup.mixed().when({
        is: () => currentStepKey === stepsDefinition.stepGeneralInformation.key,
        then: Yup.object({
          allowed_mail_domains: Yup.string()
            .required(t("common.required"))
            .min(3, "Must be at least 3 characters")
            .test("is-valid-domain", "Please enter a valid domain", (value) => {
              const domains = value.split(/\r?\n/);
              return domains.every((domain) => Utils.isValidDomain(domain));
            }),
        }),
      }),
      [stepsDefinition.stepBotSettings.key]: Yup.mixed().when({
        is: () => currentStepKey === stepsDefinition.stepBotSettings.key,
        then: Yup.object({
          bot_name: Yup.string().required(t("common.required")).min(3, "Must be at least 3 characters"),
        }),
      }),
    }),
    onSubmit: async (values) => {},
  });

  useEffect(() => {
    if (project?.id) {
      // setFirstProject(userInfo.projects[0]);
      getInvitedUsers(project.id).then((res) => {
        const list = [...new Set(res.results.map((item) => item.email))];
        setInvitedUserList(list);
        formik.setFieldValue(`${stepsDefinition.stepLivechat.key}.agentList`, list);
      });
    }
    // dispatch(getCurrentUser());
  }, [dispatch, project?.id]);

  useEffect(() => {
    document.title = t("layout.sidebar.onboarding");
    if (company?.id) {
      document.title = `${t("layout.sidebar.onboarding")} - ${company.name}`;
    }
  }, [t, dispatch, company]);

  const saveRagState = async () => {
    const sourceList = formik.values[stepsDefinition.stepRag.key].sourceList;
    const mainBotId = project.settings.main_bot;

    await dispatch(
      updateRagState(
        project.id,
        mainBotId,
        {
          source_webpages: [
            ...sourceList
              .filter((item) => item.type === onboardingRagType.webpage)
              .map((item) => ({ id: item.payload.id })),
          ],
          source_files: [
            ...sourceList
              .filter((item) => item.type === onboardingRagType.file)
              .map((item) => ({ id: item.payload.id })),
          ],
          text_content: sourceList
            .filter((item) => item.type === onboardingRagType.content)
            .map((item) => item.payload.text)
            .join(OnboardingContentSeparator),
        },
        { successMsg: false }
      )
    );
  };
  const handleClickNextOrPrevious =
    (isNextSkipOrPrevious) =>
    async (e, { preventContinue, preventRedirect } = { preventContinue: false, preventRedirect: false }) => {
      //go forward if clicked next or skip
      const directionValue = isNextSkipOrPrevious !== false ? 1 : -1;
      const isForward = directionValue === 1;
      const isSkip = isNextSkipOrPrevious === null;

      const validationResult = await formik.validateForm();
      if (Object.keys(validationResult).length > 0 && isForward) {
        formik.submitForm();
        return;
      }

      if (currentStepItem.key === stepsDefinition.stepChatbot.key) {
      }

      if (currentStepItem.key === stepsDefinition.stepBasic.key && isForward) {
        const newProject = await dispatch(
          createOrUpdateProject(
            {
              name: formik.values[stepsDefinition.stepBasic.key].assistant_name,
              id: project.id,
              main_language: formik.values[stepsDefinition.stepBasic.key].assistant_languages,
              ...(formik.values[stepsDefinition.stepBasic.key].assistant_template && {
                ...(formik.values[stepsDefinition.stepBasic.key].assistant_template_type === "shopify"
                  ? {
                      shopify_integration: formik.values[stepsDefinition.stepBasic.key].assistant_template,
                    }
                  : {
                      from_template: formik.values[stepsDefinition.stepBasic.key].assistant_template,
                    }),
              }),

              ...(!project?.shopify_integration &&
                formik.values[stepsDefinition.stepShopifyIntegration.key].shopify_integration && {
                  //Only set shopify_integration initial request if not already set. Update is not allowed for existing project.
                  shopify_integration: formik.values[stepsDefinition.stepShopifyIntegration.key].shopify_integration,
                }),
              ...(companyId && { company_id: Number(companyId) }),
            },
            { successMsg: false }
          )
        );
        if (!activeProjectId) {
          HistoryHelper.replace({
            pathname: `/dashboard/setup/${newProject.id}`,
            search: Utils.qs({
              ...searchParams,
            }),
          });
        }
      } else if (currentStepItem.key === stepsDefinition.stepGeneralInformation.key && isForward) {
        const supportedLangs = formik.values[
          stepsDefinition.stepGeneralInformation.key
        ].assistant_supported_languages.map((lang) => ({ language: lang }));
        await dispatch(
          updateProjectSettings(
            project?.id,
            {
              projectSettingsId: project?.settings?.id,
              chatbot_languages: [formik.values[stepsDefinition.stepBasic.key].assistant_languages],
              supported_languages: supportedLangs,
            },
            { successMsg: false }
          )
        );
        await dispatch(getPopupSettings(project.id));

        const allowedMailDomainArray = (
          formik.values[stepsDefinition.stepGeneralInformation.key]?.allowed_mail_domains || ""
        )
          .split(/\r?\n/)
          .map((item) => item.trim());
        const trimmedAllowedMailDomains = allowedMailDomainArray.join("\n");
        formik.setFieldValue(
          `${stepsDefinition.stepGeneralInformation.key}.allowed_mail_domains`,
          trimmedAllowedMailDomains
        );
        await dispatch(
          createOrUpdateCompany(
            {
              id: company.id,
              name: company.name,
              // address: company.address,
              company_details: formik.values[stepsDefinition.stepGeneralInformation.key].company_details,
              allowed_mail_domains: allowedMailDomainArray,
            },
            { successMsg: false }
          )
        );

        if (formik.values[stepsDefinition.stepGeneralInformation.key].company_logo_file) {
          const form = new FormData();
          form.append("id", company.id);
          form.append("name", company.name);
          form.append("company_logo", formik.values[stepsDefinition.stepGeneralInformation.key].company_logo_file);
          await dispatch(createOrUpdateCompany(form));
          formik.setFieldValue(`${stepsDefinition.stepGeneralInformation.key}.company_logo_file`, null);
        }
        await dispatch(getProject(project?.id));
      } else if (currentStepItem.key === stepsDefinition.stepBotSettings.key && isForward) {
        const mainBotId = project.settings?.main_bot;
        LoadingHelper.open();
        try {
          await updateBotSettings(project?.id, mainBotId, {
            ...botSettings,
            name: formik.values[stepsDefinition.stepBotSettings.key].bot_name,
            awareness_prompt: formik.values[stepsDefinition.stepBotSettings.key].awareness_prompt,
          });
        } catch {
        } finally {
          LoadingHelper.close();
        }
      } else if (currentStepItem.key === stepsDefinition.stepLivechat.key && isForward) {
        if (!isSkip) {
          dispatch(
            updateCallcenterSettings(
              project.id,
              {
                ...callcenterSettings,
                agent_can_leave_session: formik.values[stepsDefinition.stepLivechat.key].agent_can_leave_session,
                working_hours: {
                  start: formik.values[stepsDefinition.stepLivechat.key].work_start_time.format("HH:mm"),
                  end: formik.values[stepsDefinition.stepLivechat.key].work_end_time.format("HH:mm"),
                },
              },
              { successMsg: false }
            )
          );
          for (let i = 0; i < formik.values[stepsDefinition.stepLivechat.key].agentList.length; i++) {
            const item = formik.values[stepsDefinition.stepLivechat.key].agentList[i];
            let payload = { email: item, roles: [userRole.callCenterAgent] };

            if (!invitedUserList.includes(item)) {
              await postInviteUser(project.id, payload);
            }
          }
        }
      } else if (currentStepItem.key === stepsDefinition.stepChatbot.key) {
        setPreviewSettings(
          {
            ...formik.values.welcomeForm,
          },
          {
            botName: formik.values[stepsDefinition.stepBotSettings.key].bot_name,
          }
        );

        if (isForward) {
          const rawFileKeys = Object.keys(formik.values.welcomeForm).filter((key) => key.endsWith("___file"));
          const rawFileObj = rawFileKeys.reduce((acc, key) => {
            const keyWithoutFile = key.replace("___file", "");
            acc[keyWithoutFile] = [formik.values.welcomeForm[key]];
            return acc;
          }, {});

          await dispatch(
            patchPopupSettings(
              project.id,
              project?.settings?.id,
              formik.values.welcomeForm,
              FileHelper.getFormDataByFileObj(rawFileObj),
              { successMsg: false }
            )
          );

          // await dispatch(
          //   updateProjectSettings(
          //     project?.settings?.id,
          //     {
          //       ...formik.values.welcomeForm,
          //       chatbot_languages: formik.values[stepsDefinition.stepBasic.key].assistant_languages
          //         ? [formik.values[stepsDefinition.stepBasic.key].assistant_languages]
          //         : formik.values.welcomeForm?.chatbot_languages,
          //       supported_languages:
          //         formik.values[stepsDefinition.stepGeneralInformation.key].assistant_supported_languages ||
          //         formik.values.welcomeForm?.supported_languages,
          //     },
          //     {
          //       successMsg: false,
          //       loading: false,
          //     }
          //   )
          // );

          const mainBotId = project.settings.main_bot;
          let responseRagState = await dispatch(getRagState(project.id, mainBotId, {}));
          if (!responseRagState?.id) {
            await dispatch(
              updateRagState(
                project.id,
                mainBotId,
                {
                  source_webpages: [],
                  source_files: [],
                  text_content: "",
                },
                { successMsg: false }
              )
            );
            responseRagState = await dispatch(getRagState(project.id, mainBotId, {}));
          }
          const sourceList = [];
          sourceList.push(
            ...responseRagState.source_webpages.map((item) => ({ type: onboardingRagType.webpage, payload: item }))
          );
          sourceList.push(
            ...responseRagState.source_files.map((item) => ({ type: onboardingRagType.file, payload: item }))
          );

          if (responseRagState.text_content) {
            sourceList.push(
              ...responseRagState.text_content.split(OnboardingContentSeparator).map((item, index) => ({
                type: onboardingRagType.content,
                payload: {
                  id: index + 1,
                  text: item,
                },
              }))
            );
          }

          const responseRagIntent = await getIntentDetails(project.id, mainBotId, responseRagState.id);
          formik.setFieldValue(`${stepsDefinition.stepRag.key}.sourceList`, sourceList);
          formik.setFieldValue(`${stepsDefinition.stepRag.key}.ragIntentId`, responseRagState.id);
          formik.setFieldValue(`${stepsDefinition.stepRag.key}.ragFactGroup`, responseRagIntent.rag_fact_group);
          formik.setFieldValue(`${stepsDefinition.stepRag.key}.ragFactGroupId`, responseRagIntent.rag_fact_group?.id);
        }
      } else if (currentStepItem.key === stepsDefinition.stepRag.key && isForward) {
        await saveRagState();
      } else if (currentStepItem.key === stepsDefinition.stepRag.key && !isForward) {
      }

      if (preventContinue) return;

      //Finish step
      if (!nextStepItem && (isForward || isSkip)) {
        const isInOnboarding = project?.status === onboardingProjectStatus.ONB;
        if (isInOnboarding) {
          await dispatch(updateOnboardingStatus(project.id, { action: onboardingStatus.complete }));
        }
        // HistoryHelper.push("/dashboard");
        if (!preventRedirect) {
          window.location.href = "/dashboard";
        }
        if (isInOnboarding) {
          AlertHelper.showSuccess("Onboarding completed successfully");
        }
        return;
      }
      setCurrentStepKey(isForward || isSkip ? nextStepItem.key : previousStepItem.key);
      setDirection(directionValue);
    };

  const variants = {
    enter: (direction) => ({
      x: direction === 1 ? "100%" : "-100%",
      opacity: 0,
      position: "absolute",
      width: "100%",
    }),
    center: {
      x: 0,
      opacity: 1,
      position: "absolute",
      width: "100%",
    },
    exit: (direction) => ({
      x: direction === 1 ? "-100%" : "100%",
      opacity: 0,
      position: "absolute",
      width: "100%",
    }),
  };

  const isPopupPreviewVisible = [stepsDefinition.stepChatbot.key, stepsDefinition.stepRag.key].includes(currentStepKey);

  const showPrevious = !!previousStepItem;
  const showNext = !!nextStepItem;
  const rightSideWidth = isPopupPreviewVisible ? panelRightWidthLarge : panelRightWidthSmall;

  useEffect(() => {
    if (!isPreviewBotReady || hasIframeError) return;
    setPreviewSettings(
      {
        ...formik.values.welcomeForm,
      },
      {
        botName: formik.values[stepsDefinition.stepBotSettings.key].bot_name,
      }
    );
  }, [formik.values.welcomeForm, isPreviewBotReady, hasIframeError]);

  const handleIFrameLoad = async (e) => {
    const targetIFrame = e.target;
    const targetWindow = targetIFrame.contentWindow;
    targetWindow.addEventListener("palmate-chatbot", (event) => {
      //replay event
      const type = event?.detail?.type;
      if (type === "init") {
        const targetDocument = targetWindow.document;
        const iframe = targetDocument.getElementById("_palamar_chat_iframe");

        iframe.contentWindow.dashboard ??= {};
        iframe.contentWindow.dashboard.reloadPreviewSettings = () => {
          setIsPreviewBotReady(true);
        };
      }
    });
  };

  const handleIFrameError = (e) => {
    setHasIframeError(true);
  };

  const handleClickToggleDrawer = () => {
    setDrawerVisibility((prev) => !prev);
  };

  const handleExitOnboarding = async () => {
    if (!(await DialogHelper.showQuestionYesNo("Are you sure you want to exit the onboarding process?"))) {
      return;
    }

    HistoryHelper.push("/dashboard");

    // dispatch(stopRag(project.id, botList?.[0]?.id));
  };
  const iframeContent = (
    <Box alignItems="center" display="flex" height={1} justifyContent="center" width={1}>
      <Box
        defer
        component={"iframe"}
        id={IFRAME_PREVIEW_ID}
        src={import.meta.env.VITE_APP_PUBLIC_URL + "/dashboard/setup/bot-preview"}
        sx={{
          borderRadius: "20px",
          // borderRadius: "8px",
          // height: 650,
          width: popupChatWidthDefault,
          border: isMdDown ? "none" : "2px dashed #39393933",
          height: isMdDown ? "calc(100% - 120px)" : "calc(100% - 200px)",
          marginBottom: isMdDown ? "-40px" : "135px",
        }}
        title="Popup Preview"
        zIndex={1}
        onError={handleIFrameError}
        onLoad={handleIFrameLoad}
      />
    </Box>
  );

  return (
    <StyledDiv>
      <HeaderBarGrid container alignItems={"center"}>
        <Grid item xs>
          <LogoWrapper>
            <PalLink noPrefix to="/">
              <Logo />
            </PalLink>
          </LogoWrapper>
        </Grid>
        <Grid item xs="auto">
          <PalIconButton size="large" onClick={handleExitOnboarding}>
            <Cancel
              sx={{
                width: 32,
                height: 32,
              }}
            />
          </PalIconButton>
        </Grid>
      </HeaderBarGrid>
      <Grid container height={`calc(100% - ${headerHeight}px)`}>
        <Grid item xs>
          <Grid container flexDirection={"column"} height={1}>
            <Grid
              item
              xs
              sx={{
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <LinearProgress
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                }}
                value={(currentStepIndex / (stepsList.length - 1)) * 100}
                variant="determinate"
              />
              <Box
                alignItems={"center"}
                display={"flex"}
                height={stepbarHeight}
                justifyContent={"space-between"}
                sx={{
                  position: "sticky",
                  top: "4px",
                  backgroundColor: "#fff",
                  zIndex: 10,
                }}
              >
                <Box mx={3} my={2}>
                  <PalTypography gutterBottom color="textSecondary" fontWeight={600} variant="subtitle2">
                    Step {currentStepIndex + 1}/{stepsList.length}
                  </PalTypography>
                </Box>
                {isMdDown && currentStepKey === stepsDefinition.stepChatbot.key && (
                  <Box>
                    <PalIconButton size="large" onClick={handleClickToggleDrawer}>
                      <Menu />
                    </PalIconButton>
                  </Box>
                )}
              </Box>
              <PanelLeft>
                <AnimatePresence custom={direction} initial={false}>
                  <motion.div
                    key={currentStepKey}
                    animate="center"
                    custom={direction}
                    exit="exit"
                    initial="enter"
                    style={{
                      maxHeight: "100%",
                    }}
                    transition={{ duration: 0.3 }}
                    variants={variants}
                  >
                    {!debouncedLoadingDone && (
                      <Box
                        alignItems={"center"}
                        display={"flex"}
                        height={"100%"}
                        justifyContent={"center"}
                        width={"100%"}
                      >
                        <CircularProgress size={48} />
                      </Box>
                    )}
                    {stepsList.map((step, index) => {
                      const StepComponent = step.component;
                      const handleSetFieldValue = (key, value) => {
                        formik.setFieldValue(`${step.key}.${key}`, value);
                      };

                      const handleNextAction = async (...args) => {
                        return handleClickNextOrPrevious(true)(...args);
                      };

                      const disableBottomActions = (disabled) => {
                        setIsBottomActionsDisabled(disabled);
                      };
                      return (
                        <Fragment key={step.key}>
                          {currentStepKey === step.key && debouncedLoadingDone && (
                            <StepComponent
                              disableBottomActions={disableBottomActions}
                              formik={formik}
                              nextAction={handleNextAction}
                              setFieldValue={handleSetFieldValue}
                              stepKey={step.key}
                              {...(step.key === stepsDefinition.stepGeneralInformation.key && {
                                assistantLanguage:
                                  formik.values[stepsDefinition.stepBasic.key].assistant_languages?.toLowerCase(),
                              })}
                              {...(step.key === stepsDefinition.stepShopifyCompleted.key && {
                                shopifyIntegrationId:
                                  formik.values[stepsDefinition.stepShopifyIntegration.key].shopify_integration,
                              })}
                            />
                          )}
                        </Fragment>
                      );
                    })}
                    {!debouncedLoadingDone && <Box height={"200px"} visibility={"hidden"} />}
                  </motion.div>
                </AnimatePresence>
              </PanelLeft>
            </Grid>

            <Grid item xs="auto">
              <StepFooterContainerGrid container alignItems={"center"}>
                <Grid item xs>
                  {showPrevious && (
                    <NavigateButton
                      color="primary"
                      disabled={isBottomActionsDisabled}
                      size="medium"
                      startIcon={<NavigateBefore />}
                      variant="text"
                      onClick={handleClickNextOrPrevious(false)}
                    >
                      Previous
                    </NavigateButton>
                  )}
                </Grid>
                <Grid item xs="auto">
                  {currentStepItem?.isOptional && (
                    <>
                      <Box component={"span"} mr={2}>
                        <NavigateButton
                          color="secondary"
                          disabled={isBottomActionsDisabled}
                          size="medium"
                          variant="text"
                          onClick={handleClickNextOrPrevious(null)}
                        >
                          I'll do this later
                        </NavigateButton>
                      </Box>
                    </>
                  )}
                  <NavigateButton
                    color="primary"
                    disabled={isBottomActionsDisabled}
                    endIcon={<NavigateNext />}
                    size="medium"
                    variant="contained"
                    onClick={handleClickNextOrPrevious(true)}
                  >
                    {showNext ? "Next" : "Finish"}
                  </NavigateButton>
                </Grid>
              </StepFooterContainerGrid>
            </Grid>
          </Grid>
        </Grid>
        {!isMdDown && (
          <Grid item xs="auto">
            <motion.div
              animate={{ width: rightSideWidth }}
              style={{
                height: "100%",
              }}
              transition={{
                duration: 0.85,
                type: "spring",
                // ease: "easeInOut",
              }}
            >
              <PanelRight>
                <motion.div
                  animate={{
                    opacity: isPopupPreviewVisible ? 0 : 1,
                    // scale: isPopupPreviewVisible ? 0.5 : 1,
                  }}
                  initial={{ opacity: 1, scale: 1 }}
                  transition={{
                    duration: 0.25,
                    ease: "easeInOut",
                  }}
                >
                  <RightImgWrapper>
                    <Img1 />
                  </RightImgWrapper>
                </motion.div>

                <motion.div
                  animate={{
                    opacity: isPopupPreviewVisible ? 1 : 0,
                    scale: isPopupPreviewVisible ? 1 : 0.5,
                  }}
                  initial={{
                    height: "100%",
                    opacity: 0,
                    scale: 0.5,
                  }}
                  transition={{
                    duration: 0.25,
                    ease: "easeInOut",
                  }}
                >
                  {iframeContent}
                </motion.div>
                <RightStepInfo>
                  <PalTypography color="textSecondary" variant="body2">
                    <PalTypography component="span" fontWeight={"bold"} variant="body2">
                      Introduce Yourself&nbsp;
                    </PalTypography>
                    involves providing information about yourself and your business. Please ensure all details are
                    accurate.
                  </PalTypography>
                </RightStepInfo>
              </PanelRight>
            </motion.div>
          </Grid>
        )}

        {currentStepKey === stepsDefinition.stepChatbot.key && (
          <Drawer keepMounted anchor="right" open={drawerVisibility} onClose={handleClickToggleDrawer}>
            <PanelRight fullHeight>
              <Box height={1} padding={2} position="relative">
                <Box position="absolute" right={1} top={1}>
                  <PalIconButton size="large" onClick={handleClickToggleDrawer}>
                    <Cancel fontSize="large" />
                  </PalIconButton>
                </Box>
                {iframeContent}
              </Box>
            </PanelRight>
          </Drawer>
        )}
      </Grid>
    </StyledDiv>
  );
}

export default Onboarding;
